import React, { useEffect, useMemo, useState } from "react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";

interface Employee {
  id: number;
  name: string;
  type?: string;
  contact?: { phone?: string };
}

interface AttendanceRow {
  id: number;
  employee_id: number;
  attendance_date: string;
  status?: string;
  check_in_time?: string;
  check_out_time?: string;
  shift_start_time?: string;
  shift_end_time?: string;
  total_work_minutes?: number;
  late_minutes?: number;
  early_leave_minutes?: number;
  notes?: string;
  employee?: Employee;
}

interface ShiftTemplate {
  id: number;
  shift_name: string;
  start_time?: string;
  end_time?: string;
}

interface AssignedShift {
  id: number;
  employee_id: number;
  shift_date: string;
  start_date?: string;
  end_date?: string;
  shift_name?: string;
  start_time?: string;
  end_time?: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const formatMinutes = (minutes?: number) => {
  if (!minutes && minutes !== 0) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

const EmployeeAttendance: React.FC = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const [assignedShifts, setAssignedShifts] = useState<AssignedShift[]>([]);
  const [attendanceRows, setAttendanceRows] = useState<AttendanceRow[]>([]);
  const [activeTab, setActiveTab] = useState<"daily" | "records">("daily");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });
  const [attendanceForm, setAttendanceForm] = useState({
    employee_id: "",
    attendance_date: today(),
    shift_template_id: "",
    status: "present",
    check_in_time: "",
    check_out_time: "",
    shift_start_time: "",
    shift_end_time: "",
    notes: "",
  });

  const employeeName = useMemo(() => {
    const map = new Map<number, string>();
    employees.forEach((employee) => map.set(employee.id, employee.name));
    return map;
  }, [employees]);

  const api = async (path: string, options: RequestInit = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
      ...options,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [employeeData, shiftTemplateData, assignedShiftData, attendanceData] = await Promise.all([
        api("/api/employees"),
        api("/api/attendance/shift-templates"),
        api(`/api/attendance/shifts?shift_date=${attendanceForm.attendance_date}`),
        api(`/api/attendance?attendance_date=${attendanceForm.attendance_date}`),
      ]);
      setEmployees(employeeData);
      setShiftTemplates(shiftTemplateData);
      setAssignedShifts(assignedShiftData);
      setAttendanceRows(attendanceData);
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load attendance", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const selectShiftTemplate = (shiftTemplateId: string) => {
    const shiftTemplate = shiftTemplates.find((item) => String(item.id) === shiftTemplateId);
    setAttendanceForm((prev) => ({
      ...prev,
      shift_template_id: shiftTemplateId,
      shift_start_time: shiftTemplate?.start_time || "",
      shift_end_time: shiftTemplate?.end_time || "",
      check_in_time: shiftTemplate?.start_time || prev.check_in_time,
      check_out_time: shiftTemplate?.end_time || prev.check_out_time,
    }));
  };

  useEffect(() => {
    if (!attendanceForm.employee_id) {
      setAttendanceForm((prev) => ({ ...prev, shift_template_id: "", shift_start_time: "", shift_end_time: "" }));
      return;
    }

    const assignedShift = assignedShifts.find((shift) => String(shift.employee_id) === attendanceForm.employee_id);
    if (!assignedShift) {
      setAttendanceForm((prev) => ({ ...prev, shift_template_id: "", shift_start_time: "", shift_end_time: "" }));
      return;
    }

    const matchingTemplate = shiftTemplates.find((shiftTemplate) =>
      shiftTemplate.shift_name === assignedShift.shift_name &&
      (shiftTemplate.start_time || "") === (assignedShift.start_time || "") &&
      (shiftTemplate.end_time || "") === (assignedShift.end_time || "")
    );

    setAttendanceForm((prev) => ({
      ...prev,
      shift_template_id: matchingTemplate ? String(matchingTemplate.id) : "",
      shift_start_time: assignedShift.start_time || "",
      shift_end_time: assignedShift.end_time || "",
      check_in_time: assignedShift.start_time || prev.check_in_time,
      check_out_time: assignedShift.end_time || prev.check_out_time,
    }));
  }, [attendanceForm.employee_id, attendanceForm.attendance_date, assignedShifts, shiftTemplates]);

  useEffect(() => {
    loadData();
  }, [attendanceForm.attendance_date]);

  const saveAttendance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!attendanceForm.employee_id) {
      setModal({ open: true, title: "Missing employee", message: "Select an employee before saving attendance." });
      return;
    }

    try {
      const { shift_template_id: _shiftTemplateId, ...attendancePayload } = attendanceForm;
      await api("/api/attendance", {
        method: "POST",
        body: JSON.stringify({
          ...attendancePayload,
          employee_id: Number(attendanceForm.employee_id),
          marked_by_id: user?.employee_id || null,
        }),
      });
      setModal({ open: true, title: "Attendance saved", message: "Daily attendance was saved." });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save attendance", message: err.message || "Please try again." });
    }
  };

  return (
    <SidebarLayout title="Attendance" subtitle="Capture daily attendance and review attendance records.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />

      <section className="rounded border border-border-light bg-surface-light p-5">
        <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Attendance sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "daily"}
            onClick={() => setActiveTab("daily")}
            className={`rounded border px-4 py-2 text-sm font-semibold transition ${activeTab === "daily" ? "border-primary bg-primary text-white" : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"}`}
          >
            Daily Attendance
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "records"}
            onClick={() => setActiveTab("records")}
            className={`rounded border px-4 py-2 text-sm font-semibold transition ${activeTab === "records" ? "border-primary bg-primary text-white" : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"}`}
          >
            Attendance Records
          </button>
        </div>

        {activeTab === "daily" && (
          <form onSubmit={saveAttendance}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label>
              <div className="mb-1 text-sm font-semibold">Employee</div>
              <select required value={attendanceForm.employee_id} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, employee_id: e.target.value }))} className="w-full rounded border p-3">
                <option value="">Select employee</option>
                {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
              </select>
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Date</div>
              <input type="date" value={attendanceForm.attendance_date} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, attendance_date: e.target.value }))} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Status</div>
              <select value={attendanceForm.status} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, status: e.target.value }))} className="w-full rounded border p-3">
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="half_day">Half Day</option>
                <option value="leave">Leave</option>
              </select>
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Shift</div>
              <select value={attendanceForm.shift_template_id} onChange={(e) => selectShiftTemplate(e.target.value)} className="w-full rounded border p-3">
                <option value="">Select shift</option>
                {shiftTemplates.map((shiftTemplate) => (
                  <option key={shiftTemplate.id} value={shiftTemplate.id}>
                    {shiftTemplate.shift_name} ({shiftTemplate.start_time || "-"} - {shiftTemplate.end_time || "-"})
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Check In</div>
              <input type="time" value={attendanceForm.check_in_time} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, check_in_time: e.target.value }))} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Check Out</div>
              <input type="time" value={attendanceForm.check_out_time} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, check_out_time: e.target.value }))} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Shift Start</div>
              <input type="time" value={attendanceForm.shift_start_time} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, shift_start_time: e.target.value }))} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Shift End</div>
              <input type="time" value={attendanceForm.shift_end_time} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, shift_end_time: e.target.value }))} className="w-full rounded border p-3" />
            </label>
            <label className="md:col-span-2">
              <div className="mb-1 text-sm font-semibold">Notes</div>
              <textarea value={attendanceForm.notes} onChange={(e) => setAttendanceForm((prev) => ({ ...prev, notes: e.target.value }))} className="min-h-20 w-full rounded border p-3" />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" className="rounded bg-primary px-5 py-2 font-semibold text-white">Save Attendance</button>
          </div>
          </form>
        )}

        {activeTab === "records" && (
          loading ? <div>Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-background-light text-xs uppercase text-subtext-light">
                  <tr>
                    <th className="p-2">Employee</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">In</th>
                    <th className="p-2">Out</th>
                    <th className="p-2">Worked</th>
                    <th className="p-2">Late</th>
                    <th className="p-2">Early Leave</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRows.map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="p-2 font-semibold">{row.employee?.name || employeeName.get(row.employee_id) || row.employee_id}</td>
                      <td className="p-2 capitalize">{row.status || "-"}</td>
                      <td className="p-2">{row.check_in_time || "-"}</td>
                      <td className="p-2">{row.check_out_time || "-"}</td>
                      <td className="p-2">{formatMinutes(row.total_work_minutes)}</td>
                      <td className="p-2">{formatMinutes(row.late_minutes)}</td>
                      <td className="p-2">{formatMinutes(row.early_leave_minutes)}</td>
                    </tr>
                  ))}
                  {!attendanceRows.length && <tr><td colSpan={7} className="p-4 text-center text-subtext-light">No attendance records for this date.</td></tr>}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>
    </SidebarLayout>
  );
};

export default EmployeeAttendance;
